import { z } from "zod";

/**
 * Recursive JSON value schema — validates that a value is a valid JSON primitive,
 * object, or array. Used for `additionalContext` fields in deployment session metadata.
 *
 * Typed as `unknown` because TypeScript cannot express the recursive `JsonValue` type
 * through `z.lazy()` without hitting instantiation depth limits (known TypeScript
 * limitation with Zod recursive schemas exported via @nexus/types client bundle).
 * The cast `as Prisma.InputJsonValue` is required at the Prisma write boundary.
 */
export const JsonValueSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema)
  ])
);

/**
 * Size-bounded additional context schema for metadata fields.
 *
 * Accepts arbitrary JSON key-value pairs (needed for external webhook payloads)
 * but enforces hard limits to prevent metadata injection / storage abuse:
 * - Max 20 top-level keys
 * - Max 10 KB serialized size
 */
const ADDITIONAL_CONTEXT_MAX_KEYS = 20;
const ADDITIONAL_CONTEXT_MAX_BYTES = 10_240; // 10 KB

export const BoundedAdditionalContextSchema = z
  .record(z.string(), JsonValueSchema.optional())
  .optional()
  .refine(
    (val) => {
      if (val == null) return true;
      return Object.keys(val).length <= ADDITIONAL_CONTEXT_MAX_KEYS;
    },
    {
      message: `additionalContext must have at most ${ADDITIONAL_CONTEXT_MAX_KEYS} keys`
    }
  )
  .refine(
    (val) => {
      if (val == null) return true;
      return JSON.stringify(val).length <= ADDITIONAL_CONTEXT_MAX_BYTES;
    },
    {
      message: `additionalContext must not exceed ${ADDITIONAL_CONTEXT_MAX_BYTES} bytes when serialized`
    }
  );
